import { useState, useEffect } from 'react'
import {Copy, Check, Download, Link as LinkIcon, MessageCircleDashed as MessageCircle, Users, Image as ImageIcon, GraduationCap, QrCode, FolderOpen, ExternalLink, FileText, UserCheck} from 'lucide-react'
import toast from 'react-hot-toast'
import QrCodesPersonalizados from '@/components/QrCodesPersonalizados'
import FolderPersonalizado from '@/components/FolderPersonalizado'
import PanfletoPequeno from '@/components/PanfletoPequeno'
import PanfletoKids from '@/components/PanfletoKids'
import { lumi } from '@/lib/lumi'
import { copiarTexto } from '@/utils/clipboard'

interface DivulgarLinkProps {
  usuarioConsultora: string
  /** Nome de exibicao da consultora (default = usuarioConsultora). */
  nomeConsultora?: string
  /**
   * Define se a consultora tem acesso ao bloco de Indicar para a Capacitacao.
   * Regra: liberado para statusAssinatura mensal/anual e gratuito (equipe Monica).
   * Default: true (compatibilidade).
   */
  acessoCapacitacao?: boolean
}

// Criativos — serão substituídos pelos arquivos reais após upload
const CRIATIVO_1_URL = 'https://static.lumi.new/de/de8edd3ba28dbda34bc6409de04d274a.webp'
const CRIATIVO_2_URL = 'https://static.lumi.new/c6/c6fa09f9e5f6defc880425c3fa71946b.webp'

const CHAVE_LINK_MATERIAIS = 'link_materiais_divulgacao'

async function baixarArquivo(url: string, nomeArquivo: string) {
  try {
    const resposta = await fetch(url)
    const blob = await resposta.blob()
    const blobUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = nomeArquivo
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000)
  } catch {
    window.open(url, '_blank')
  }
}

function CopiarBotao({
  texto,
  label,
  className = '',
}: {
  texto: string
  label: string
  className?: string
}) {
  const [copiado, setCopiado] = useState(false)

  const copiar = async () => {
    try {
      await copiarTexto(texto)
      setCopiado(true)
      toast.success('Copiado!')
      setTimeout(() => setCopiado(false), 2000)
    } catch {
      toast.error('Erro ao copiar')
    }
  }

  return (
    <button
      onClick={copiar}
      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all min-h-[44px] ${
        copiado
          ? 'bg-verde-muito-claro text-verde-escuro border-2 border-verde-escuro'
          : 'bg-verde-escuro text-white hover:bg-opacity-90'
      } ${className}`}
    >
      {copiado ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      {copiado ? 'Copiado!' : label}
    </button>
  )
}

function OrientacaoBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-[#F0FDF4] border border-[#86EFAC] rounded-xl px-4 py-3 text-sm text-[#15803D] leading-relaxed mb-5">
      {children}
    </div>
  )
}

function MensagemBox({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="bg-bege-fundo border border-bege-medio rounded-xl px-4 py-4 text-sm text-texto-escuro leading-relaxed whitespace-pre-wrap font-mono mb-3"
      style={{ fontFamily: 'DM Sans, sans-serif' }}
    >
      {children}
    </div>
  )
}

export default function DivulgarLink({ usuarioConsultora, nomeConsultora, acessoCapacitacao = true }: DivulgarLinkProps) {
  const linkPersonalizado = `https://www.descubraseusoleos.com.br/?c=${usuarioConsultora}`

  // Carrega o link de "Outros Materiais" da coleção configuracoes (configurável pelo admin)
  const [linkMateriais, setLinkMateriais] = useState<string>('')
  const [carregandoMateriais, setCarregandoMateriais] = useState(true)

  useEffect(() => {
    let ativo = true
    ;(async () => {
      try {
        const resp: any = await lumi.entities.configuracoes.list({
          filter: { chave: CHAVE_LINK_MATERIAIS },
          limit: 1,
        })
        const lista = resp?.list ?? []
        if (ativo && lista.length > 0) {
          setLinkMateriais((lista[0]?.valor as string) || '')
        }
      } catch (e) {
        console.error('Erro ao carregar link de materiais:', e)
      } finally {
        if (ativo) setCarregandoMateriais(false)
      }
    })()
    return () => {
      ativo = false
    }
  }, [])

  // ── ABORDAGEM 1 (Autoconhecimento) — 1 a 1 ──
  const a1Mensagem1 = `Oi [nome]! 🥰\n\nVocê quer descobrir de forma gratuita o que está afetando sua energia, emoções e bem-estar?`

  const a1Mensagem2 = `🔎 Diagnóstico GRATUITO de Saúde e Emoções\n\nEm 5 minutos você descobre o que está comprometendo sua saúde física e emocional e recebe uma orientação personalizada para equilibrar de forma natural.\n\nO resultado sai na hora!\n\n${linkPersonalizado}`

  const a1Mensagem3 = `Assim que você preencher eu já vejo seu resultado e te chamo aqui no WhatsApp para te orientar, ok? 🌿`

  // ── ABORDAGEM 2 (Óleos essenciais) — 1 a 1 ──
  // Mensagem 1 — sem link
  const mensagem1 = `Oi [nome], 🥰\n\nTudo bem!?\n\n🎁💧 Você quer descobrir de forma gratuita e personalizada quais óleos essenciais o seu corpo e mente precisam para sair da ansiedade, procrastinação, cansaço e até dos desconfortos físicos?`

  // Mensagem 2 — com link
  const mensagem2 = `🔎🎁 Diagnóstico GRATUITO de Saúde e Emoções\n\nLeva apenas 5 minutos e o resultado sai na hora!\n\n${linkPersonalizado}`

  // Mensagem 3 — após enviar o link, cria expectativa
  const mensagem3 = `Assim que você preencher o diagnóstico eu já vou preparar o seu resultado personalizado e te chamo aqui no WhatsApp, ok?!`

  // ── ABORDAGEM 1 (Autoconhecimento) — Grupos ──
  const a1MensagemGrupos = `🌿 Diagnóstico GRATUITO de Saúde e Emoções\n\nDescubra o que está afetando sua energia, emoções e bem-estar e receba uma orientação personalizada para equilibrar sua saúde de forma natural.\n\nO resultado sai na hora!\n\n👉 Clique no link e preencha agora. ⬇️\n\n${linkPersonalizado}`

  // ── ABORDAGEM 2 (Óleos essenciais) — Grupos ──
  const mensagemGrupos = `🎁💡Diagnóstico de Saúde e Emoções — GRATUITO\n\nDescubra quais óleos essenciais o seu corpo e mente precisam para sair da ansiedade, procrastinação, cansaço e até dos desconfortos físicos.\n\n😍 O resultado sai na hora!\n\n👉 Clique no link abaixo e preencha agora. ⬇️\n\n${linkPersonalizado}`

  // Legenda para stories
  const legendaStories = `Clique no link abaixo e preencha agora ⬇️\n\n${linkPersonalizado}`

  return (
    <div className="space-y-8">

      {/* ── 1º BLOCO — SEU LINK PERSONALIZADO (Diagnóstico adulto) ── */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="bg-verde-escuro px-6 py-4">
          <div className="flex items-center gap-3">
            <LinkIcon className="w-5 h-5 text-white flex-shrink-0" />
            <h2 className="text-xl font-cormorant font-semibold text-white">
              Seu link personalizado
            </h2>
          </div>
        </div>
        <div className="p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-[#D8F3DC] border-2 border-verde-escuro rounded-2xl px-5 py-4 mb-4">
            <div className="flex-1 min-w-0">
              <div className="text-xs text-verde-escuro font-medium uppercase tracking-wide mb-1">
                Seu link personalizado
              </div>
              <div className="text-verde-escuro font-medium text-sm sm:text-base break-all">
                {linkPersonalizado}
              </div>
            </div>
            <CopiarBotao
              texto={linkPersonalizado}
              label="Copiar link"
              className="flex-shrink-0 w-full sm:w-auto"
            />
          </div>
          <p className="text-sm text-texto-suave leading-relaxed">
            Compartilhe esse link com seus clientes. Quando alguém preencher o diagnóstico pelo seu link, os resultados chegam automaticamente no seu painel.
          </p>
        </div>
      </div>

      {/* ── 2º BLOCO — UM A UM (WHATSAPP DIRETO) ── */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="bg-[#128C7E] px-6 py-4">
          <div className="flex items-center gap-3">
            <MessageCircle className="w-5 h-5 text-white flex-shrink-0" />
            <h2 className="text-xl font-cormorant font-semibold text-white">
              Um a um — WhatsApp direto
            </h2>
          </div>
        </div>
        <div className="p-6">
          <OrientacaoBox>
            Essa é a estratégia com maior taxa de conversão. Envie para pessoas que você conhece, uma por vez. <strong>Personalize sempre com o nome da pessoa.</strong>
          </OrientacaoBox>

          {/* ABORDAGEM 1 — FOCO NO AUTOCONHECIMENTO */}
          <div className="mb-8 border-2 border-verde-escuro rounded-2xl p-5">
            <h3 className="text-lg font-cormorant font-semibold text-verde-escuro mb-1">
              Abordagem 1 — Foco no autoconhecimento
            </h3>
            <p className="text-xs italic text-texto-suave mb-5">
              Recomendada para quem ainda não conhece os óleos essenciais
            </p>

            {/* Mensagem 1 */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-verde-escuro text-white text-xs font-bold flex-shrink-0">1</span>
                <p className="text-sm font-medium text-texto-escuro">
                  Mensagem 1 — Envie primeiro, <strong>sem o link</strong>. Aguarde a resposta antes de enviar a próxima mensagem.
                </p>
              </div>
              <MensagemBox>{a1Mensagem1}</MensagemBox>
              <CopiarBotao texto={a1Mensagem1} label="Copiar Mensagem 1" />
            </div>

            {/* Mensagem 2 */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-verde-escuro text-white text-xs font-bold flex-shrink-0">2</span>
                <p className="text-sm font-medium text-texto-escuro">
                  Mensagem 2 — Somente após a pessoa confirmar interesse, envie <strong>com o link</strong>:
                </p>
              </div>
              <MensagemBox>{a1Mensagem2}</MensagemBox>
              <CopiarBotao texto={a1Mensagem2} label="Copiar Mensagem 2 com link" />
            </div>

            {/* Mensagem 3 */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-verde-escuro text-white text-xs font-bold flex-shrink-0">3</span>
                <p className="text-sm font-medium text-texto-escuro">
                  Mensagem 3 — Após enviar o link, <strong>crie expectativa</strong>:
                </p>
              </div>
              <MensagemBox>{a1Mensagem3}</MensagemBox>
              <CopiarBotao texto={a1Mensagem3} label="Copiar Mensagem 3" />
            </div>
          </div>

          {/* ABORDAGEM 2 — FOCO NOS ÓLEOS ESSENCIAIS */}
          <div className="border-2 border-bege-medio rounded-2xl p-5">
            <h3 className="text-lg font-cormorant font-semibold text-verde-escuro mb-1">
              Abordagem 2 — Foco nos óleos essenciais
            </h3>
            <p className="text-xs italic text-texto-suave mb-5">
              Recomendada para quem já conhece ou já demonstrou interesse nos óleos essenciais
            </p>

            {/* Mensagem 1 */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-verde-escuro text-white text-xs font-bold flex-shrink-0">1</span>
                <p className="text-sm font-medium text-texto-escuro">
                  Mensagem 1 — Envie primeiro, <strong>sem o link</strong>. Aguarde a resposta antes de enviar a próxima mensagem.
                </p>
              </div>
              <MensagemBox>{mensagem1}</MensagemBox>
              <CopiarBotao texto={mensagem1} label="Copiar Mensagem 1" />
              <div
                className="mt-3 flex items-start gap-2 px-4 py-3 rounded-xl text-sm leading-relaxed"
                style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
              >
                <span className="text-base flex-shrink-0">⚠️</span>
                <span>
                  <strong>Aguarde a pessoa responder com interesse</strong> antes de enviar a próxima mensagem.
                </span>
              </div>
            </div>

            {/* Mensagem 2 */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-verde-escuro text-white text-xs font-bold flex-shrink-0">2</span>
                <p className="text-sm font-medium text-texto-escuro">
                  Mensagem 2 — Somente após a pessoa confirmar interesse, envie <strong>com o link</strong>:
                </p>
              </div>
              <MensagemBox>{mensagem2}</MensagemBox>
              <CopiarBotao texto={mensagem2} label="Copiar Mensagem 2 com link" />
            </div>

            {/* Mensagem 3 */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-verde-escuro text-white text-xs font-bold flex-shrink-0">3</span>
                <p className="text-sm font-medium text-texto-escuro">
                  Mensagem 3 — Após enviar o link, <strong>crie expectativa</strong>:
                </p>
              </div>
              <MensagemBox>{mensagem3}</MensagemBox>
              <CopiarBotao texto={mensagem3} label="Copiar Mensagem 3" />
              <div
                className="mt-3 flex items-start gap-2 px-4 py-3 rounded-xl text-sm leading-relaxed"
                style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
              >
                <span className="text-base flex-shrink-0">💡</span>
                <span>
                  Essa mensagem <strong>cria expectativa e aumenta a taxa de preenchimento</strong>. A pessoa sabe que alguém vai analisar e chamar pessoalmente.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 3º BLOCO — GRUPOS DE WHATSAPP ── */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="bg-[#25D366] px-6 py-4">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-white flex-shrink-0" />
            <h2 className="text-xl font-cormorant font-semibold text-white">
              Grupos de WhatsApp
            </h2>
          </div>
        </div>
        <div className="p-6">
          <OrientacaoBox>
            Use apenas em grupos onde você tem autorização para compartilhar links e divulgações. <strong>Nunca envie em grupos sem permissão do administrador.</strong>
          </OrientacaoBox>

          {/* ABORDAGEM 1 */}
          <div className="mb-8 border-2 border-verde-escuro rounded-2xl p-5">
            <h3 className="text-lg font-cormorant font-semibold text-verde-escuro mb-1">
              Abordagem 1 — Foco no autoconhecimento
            </h3>
            <p className="text-xs italic text-texto-suave mb-5">
              Recomendada para quem ainda não conhece os óleos essenciais
            </p>
            <MensagemBox>{a1MensagemGrupos}</MensagemBox>
            <CopiarBotao texto={a1MensagemGrupos} label="Copiar mensagem para grupos" />
          </div>

          {/* ABORDAGEM 2 */}
          <div className="border-2 border-bege-medio rounded-2xl p-5">
            <h3 className="text-lg font-cormorant font-semibold text-verde-escuro mb-1">
              Abordagem 2 — Foco nos óleos essenciais
            </h3>
            <p className="text-xs italic text-texto-suave mb-5">
              Recomendada para quem já conhece ou já demonstrou interesse nos óleos essenciais
            </p>
            <MensagemBox>{mensagemGrupos}</MensagemBox>
            <CopiarBotao texto={mensagemGrupos} label="Copiar mensagem para grupos" />
          </div>
        </div>
      </div>

      {/* ── 4º BLOCO — DIAGNÓSTICO KIDS ── */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="bg-[#B08968] px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="text-white text-xl flex-shrink-0">👶</span>
            <h2 className="text-xl font-cormorant font-semibold text-white">
              Diagnóstico Kids
            </h2>
          </div>
        </div>
        <div className="p-6">
          <p className="text-sm text-texto-escuro leading-relaxed mb-4">
            Compartilhe este link para mães e responsáveis descobrirem quais óleos essenciais são indicados para seus filhos.
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-[#FFF7ED] border-2 border-[#B08968] rounded-2xl px-5 py-4 mb-4">
            <div className="flex-1 min-w-0">
              <div className="text-xs text-[#B08968] font-medium uppercase tracking-wide mb-1">
                Link Kids personalizado
              </div>
              <div className="text-[#2C2C2A] font-medium text-sm sm:text-base break-all">
                {`https://www.descubraseusoleos.com.br/kids?c=${usuarioConsultora}`}
              </div>
            </div>
            <CopiarBotao
              texto={`https://www.descubraseusoleos.com.br/kids?c=${usuarioConsultora}`}
              label="Copiar link Kids"
              className="flex-shrink-0 w-full sm:w-auto"
            />
          </div>
          <div className="bg-[#F8F4EF] border border-[#EDE0D4] rounded-xl px-4 py-3 mb-4">
            <p className="text-sm font-medium text-texto-escuro mb-2">Mensagem sugerida com link:</p>
            <div className="text-sm text-texto-suave whitespace-pre-wrap font-mono leading-relaxed" style={{ fontFamily: 'DM Sans, sans-serif' }}>{`Oi [nome]! 🌿\n\nVocê sabia que os óleos essenciais também são indicados para crianças?\n\nA dōTERRA tem um Kit Kids específico com 7 mixes em roll-on prontos para usar e seguros para todas as idades.\n\nFiz um diagnóstico gratuito para descobrir quais são os mais indicados para o seu filho.\n\nClica no link abaixo e preencha em menos de 3 minutos:\nhttps://www.descubraseusoleos.com.br/kids?c=${usuarioConsultora}`}</div>
          </div>
          <CopiarBotao
            texto={`Oi [nome]! 🌿\n\nVocê sabia que os óleos essenciais também são indicados para crianças?\n\nA dōTERRA tem um Kit Kids específico com 7 mixes em roll-on prontos para usar e seguros para todas as idades.\n\nFiz um diagnóstico gratuito para descobrir quais são os mais indicados para o seu filho.\n\nClica no link abaixo e preencha em menos de 3 minutos:\nhttps://www.descubraseusoleos.com.br/kids?c=${usuarioConsultora}`}
            label="Copiar mensagem com link"
          />
        </div>
      </div>


      {/* ── 6º BLOCO — QR CODES PERSONALIZADOS ── */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="bg-[#2D6A4F] px-6 py-4">
          <div className="flex items-center gap-3">
            <QrCode className="w-5 h-5 text-white flex-shrink-0" />
            <h2 className="text-xl font-cormorant font-semibold text-white">
              Seus QR Codes Personalizados
            </h2>
          </div>
        </div>
        <div className="p-6">
          <QrCodesPersonalizados usuarioConsultora={usuarioConsultora} />
        </div>
      </div>

      {/* ── 7º BLOCO — MEU PANFLETO PERSONALIZADO (A4) ── */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="bg-verde-escuro px-6 py-4">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-white flex-shrink-0" />
            <h2 className="text-xl font-cormorant font-semibold text-white">
              Meu Panfleto Personalizado
            </h2>
          </div>
        </div>
        <div className="p-6">
          <FolderPersonalizado usuarioConsultora={usuarioConsultora} />
        </div>
      </div>

      {/* ── 8º BLOCO — MEU PANFLETO PEQUENO (A5) ── */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="bg-verde-escuro px-6 py-4">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-white flex-shrink-0" />
            <h2 className="text-xl font-cormorant font-semibold text-white">
              Meu Panfleto Pequeno
            </h2>
          </div>
        </div>
        <div className="p-6">
          <PanfletoPequeno usuarioConsultora={usuarioConsultora} />
        </div>
      </div>

      {/* ── 9º BLOCO — MEU PANFLETO PARA CRIANÇAS ── */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="bg-[#B08968] px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="text-white text-xl flex-shrink-0">👶</span>
            <h2 className="text-xl font-cormorant font-semibold text-white">
              Meu Panfleto para Crianças
            </h2>
          </div>
        </div>
        <div className="p-6">
          <PanfletoKids />
        </div>
      </div>

      {/* ── 10º BLOCO — STORIES E STATUS ── */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="bg-[#833AB4] px-6 py-4">
          <div className="flex items-center gap-3">
            <ImageIcon className="w-5 h-5 text-white flex-shrink-0" />
            <h2 className="text-xl font-cormorant font-semibold text-white">
              Stories e Status
            </h2>
          </div>
        </div>
        <div className="p-6">
          <OrientacaoBox>
            Use os criativos abaixo nos seus stories e status. Adicione seu link personalizado na legenda ou no link da bio. <strong>Poste com frequência</strong> pois stories desaparecem em 24 horas.
          </OrientacaoBox>

          {/* Legenda */}
          <div className="mb-8">
            <p className="text-sm font-medium text-texto-escuro mb-2">
              Texto sugerido para legenda:
            </p>
            <MensagemBox>{legendaStories}</MensagemBox>
            <CopiarBotao texto={legendaStories} label="Copiar texto para legenda" />
          </div>

          {/* Criativos */}
          <div>
            <p className="text-sm font-semibold text-texto-escuro uppercase tracking-wide mb-4">
              Criativos disponíveis para download:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">

              {/* Criativo 1 */}
              <div className="border-2 border-bege-medio rounded-2xl overflow-hidden">
                <div className="aspect-[9/16] bg-[#D8F3DC] flex items-center justify-center relative overflow-hidden">
                  <img
                    src={CRIATIVO_1_URL}
                    alt="Stories — Seu corpo fala (versão floresta)"
                    className="w-full h-full object-cover"
                    onError={e => {
                      const t = e.currentTarget
                      t.style.display = 'none'
                      const parent = t.parentElement
                      if (parent) {
                        parent.innerHTML = `<div class="flex flex-col items-center justify-center w-full h-full gap-3 text-verde-escuro p-6 text-center">
                          <svg xmlns="http://www.w3.org/2000/svg" class="w-12 h-12 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                          <span class="text-xs opacity-60">Criativo 1 em breve</span>
                        </div>`
                      }
                    }}
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm font-medium text-texto-escuro mb-1">
                    Stories — Seu corpo fala (versão floresta)
                  </p>
                  <p className="text-xs text-texto-suave mb-3 leading-relaxed">
                    Mulher segurando frasco de óleo essencial em natureza verde com texto "Seu corpo fala. Você está ouvindo?" e badge GRATUITO
                  </p>
                  <button
                    onClick={() => baixarArquivo(CRIATIVO_1_URL, 'stories-floresta.jpg')}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm bg-verde-escuro text-white hover:bg-opacity-90 transition-all min-h-[44px]"
                  >
                    <Download className="w-4 h-4" />
                    Baixar criativo 1
                  </button>
                </div>
              </div>

              {/* Criativo 2 */}
              <div className="border-2 border-bege-medio rounded-2xl overflow-hidden">
                <div className="aspect-[9/16] bg-[#D8F3DC] flex items-center justify-center relative overflow-hidden">
                  <img
                    src={CRIATIVO_2_URL}
                    alt="Stories — Seu corpo fala (versão meditação)"
                    className="w-full h-full object-cover"
                    onError={e => {
                      const t = e.currentTarget
                      t.style.display = 'none'
                      const parent = t.parentElement
                      if (parent) {
                        parent.innerHTML = `<div class="flex flex-col items-center justify-center w-full h-full gap-3 text-verde-escuro p-6 text-center">
                          <svg xmlns="http://www.w3.org/2000/svg" class="w-12 h-12 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                          <span class="text-xs opacity-60">Criativo 2 em breve</span>
                        </div>`
                      }
                    }}
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm font-medium text-texto-escuro mb-1">
                    Stories — Seu corpo fala (versão meditação)
                  </p>
                  <p className="text-xs text-texto-suave mb-3 leading-relaxed">
                    Mulher meditando em natureza verde com frasco luminoso e texto "Seu corpo fala. Você está ouvindo?" e badge GRATUITO
                  </p>
                  <button
                    onClick={() => baixarArquivo(CRIATIVO_2_URL, 'stories-meditacao.jpg')}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm bg-verde-escuro text-white hover:bg-opacity-90 transition-all min-h-[44px]"
                  >
                    <Download className="w-4 h-4" />
                    Baixar criativo 2
                  </button>
                </div>
              </div>

            </div>

            <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-xl px-4 py-3 text-sm text-[#92400E] leading-relaxed">
              💡 Após baixar o criativo, adicione seu link personalizado na legenda do story ou no link da bio do seu perfil.
            </div>
          </div>
        </div>
      </div>

      {/* ── 11º BLOCO — OUTROS MATERIAIS ── */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="bg-[#B08968] px-6 py-4">
          <div className="flex items-center gap-3">
            <FolderOpen className="w-5 h-5 text-white flex-shrink-0" />
            <h2 className="text-xl font-cormorant font-semibold text-white">
              Outros Materiais
            </h2>
          </div>
        </div>
        <div className="p-6">
          <p className="text-sm text-texto-escuro leading-relaxed mb-5">
            Acesse materiais extras para apoiar sua divulgação: imagens, criativos prontos, roteiros e muito mais.
          </p>

          {carregandoMateriais ? (
            <button
              disabled
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium text-sm bg-bege-medio text-texto-suave min-h-[44px] cursor-not-allowed"
            >
              Carregando...
            </button>
          ) : linkMateriais ? (
            <a
              href={linkMateriais}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-sm bg-verde-escuro text-white hover:bg-opacity-90 transition-all min-h-[44px]"
            >
              <ExternalLink className="w-4 h-4" />
              Acessar Materiais
            </a>
          ) : (
            <div>
              <button
                disabled
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-sm bg-bege-medio text-texto-suave min-h-[44px] cursor-not-allowed opacity-70"
              >
                <ExternalLink className="w-4 h-4" />
                Acessar Materiais
              </button>
              <div className="mt-3 bg-[#FFF7ED] border border-[#FED7AA] rounded-xl px-4 py-3 text-sm text-[#92400E] leading-relaxed">
                ⏳ <strong>Em breve.</strong> A Mônica está preparando os materiais e em breve você poderá acessá-los por aqui.
              </div>
            </div>
          )}
        </div>
      </div>


    </div>
  )
}
